import { useUIStore } from '@/config/stores/ui-store';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { Collapse, Drawer as MuiDrawer, Stack, useMediaQuery, useTheme } from '@mui/material';
import { type FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConversationMembers } from '../../queries/member.queries';
import { ConversationSettingDialog } from '../conversation-setting-dialog';

import { chatEE } from '@/layout';
import { ASIDE_WIDTH } from '@/layout/layout.constant';
import { ErrorBoundary } from '@/shared/components';
import type { MemberRole } from '../../models/member';
import ConverstationTabs from '../conversation-tabs/conversation-tabs';
import { ConversationAddMemberFab } from './conversation-add-member-fab';
import ConversationAsideHeader from './conversation-aside-header';
import { ConversationAsideInfo } from './conversation-aside-info';
import { ConversationDeleteFab } from './conversation-delete-fab';

interface ConversationAsideProps {
  className?: string;
  currentUserRole?: MemberRole;
  onClose: () => void;
  container?: any;
}

export const ConversationAside: FC<ConversationAsideProps> = memo(({ onClose, container }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xl'));
  const { activeConversation, asideExpanded } = useUIStore();
  const { user } = useAuthStore();
  const { data: members } = useConversationMembers(activeConversation?.conversationId);

  const [infoVisible, setInfoVisible] = useState(true);
  const [activeTab, setActiveTab] = useState<'member' | 'media' | 'file' | 'link'>('member');

  // Use refs to avoid re-creating callbacks
  const handleHideInfo = useCallback(() => setInfoVisible(false), []);
  const handleShowInfo = useCallback(() => setInfoVisible(true), []);

  // Memoize derived values to prevent unnecessary recalculations
  const [curUser, memberCount] = useMemo(() => {
    const curUser = (members || []).find((m) => m.userId === user?.user_id);
    return [curUser, members?.length || 0];
  }, [members, user?.user_id, activeConversation?.conversationType]);

  // Memoize event handler to avoid re-creating on every render
  const handleBackToInfo = useCallback(() => {
    setInfoVisible(true);
    setActiveTab('member');
  }, []);

  // Back-to-info event with proper cleanup
  useEffect(() => {
    chatEE.on('aside-back-to-info', handleBackToInfo);
    return () => {
      chatEE.off('aside-back-to-info', handleBackToInfo);
    };
  }, [handleBackToInfo]);

  // Reset state when conversation changes
  useEffect(() => {
    if (!activeConversation) return;

    if (activeConversation.conversationType === 'DIRECT') {
      setActiveTab('media');
    } else {
      setActiveTab('member');
    }
    setInfoVisible(true);
  }, [activeConversation?.conversationId, activeConversation?.conversationType, asideExpanded]);

  // Memoize header title to avoid recalculating on every render
  const headerTitle = useMemo(() => {
    if (infoVisible) return undefined;

    const titles = {
      member: t('conversations.tabs.member'),
      media: t('conversations.tabs.media'),
      file: t('conversations.tabs.file'),
      link: t('conversations.tabs.link')
    };

    return titles[activeTab];
  }, [infoVisible, activeTab, t]);

  // Memoize tab change handler
  const handleTabChange = useCallback(
    (key: string) => {
      handleHideInfo();
      setActiveTab(key as 'member' | 'media' | 'file' | 'link');
    },
    [handleHideInfo]
  );

  if (!activeConversation) return null;

  // Memoize drawer sx prop to prevent unnecessary style recalculations
  const drawerSx = useMemo(
    () => ({
      width: asideExpanded ? ASIDE_WIDTH : 0,
      '& .MuiDrawer-paper': {
        ...(isMobile
          ? {}
          : {
              position: 'absolute',
              top: 0,
              right: 0
            }),
        width: asideExpanded ? ASIDE_WIDTH : 0,
        minWidth: 0,
        height: '100%',
        boxSizing: 'border-box',
        borderLeft: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        overflow: 'hidden',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen
        })
      }
    }),
    [asideExpanded, isMobile, theme]
  );

  // Memoize modal props to prevent unnecessary re-renders
  const modalProps = useMemo(
    () => ({
      container: container ? () => container : undefined,
      disablePortal: true,
      keepMounted: true,
      disableScrollLock: true,
      disableAutoFocus: true,
      disableEnforceFocus: true
    }),
    [container]
  );

  return (
    <ErrorBoundary>
      <MuiDrawer
        container={container || undefined}
        variant={isMobile ? 'temporary' : 'permanent'}
        anchor='right'
        open={asideExpanded}
        onClose={onClose}
        hideBackdrop={!isMobile}
        className={`mex:transition-all mex:duration-300 mex:ease-in-out ${isMobile ? 'mex:z-50' : 'mex:z-10'}`}
        sx={drawerSx}
        ModalProps={modalProps}
      >
        <ConversationAsideHeader open={asideExpanded} conversation={activeConversation} headerTitle={headerTitle} />
        <div className='mex:flex mex:h-full mex:flex-col mex:overflow-hidden'>
          <Collapse in={infoVisible} timeout={300} unmountOnExit>
            <ConversationAsideInfo
              conv={activeConversation}
              curUser={curUser!}
              onClose={isMobile ? onClose : undefined}
            />
          </Collapse>
          <div className='mex:min-h-0 mex:flex-1 mex:pt-1'>
            <ConverstationTabs
              conversationId={activeConversation.conversationId!}
              hideMember={activeConversation.conversationType === 'DIRECT'}
              members={members || []}
              memberCount={memberCount}
              curUser={curUser!}
              onTabChange={handleTabChange}
              onPanelScrollUp={handleShowInfo}
              onPanelScrollDown={handleHideInfo}
            />
          </div>
          <div className='mex:absolute mex:right-4 mex:bottom-4'>
            <Stack gap={1}>
              <ConversationDeleteFab
                conversationId={activeConversation.conversationId!}
                conversationType={activeConversation.conversationType!}
              />
              <ConversationAddMemberFab
                conversationId={activeConversation.conversationId!}
                conversationType={activeConversation.conversationType!}
              />
            </Stack>
          </div>
        </div>
        <ConversationSettingDialog />
      </MuiDrawer>
    </ErrorBoundary>
  );
});

ConversationAside.displayName = 'ConversationAside';
